import React, { FC, useState } from 'react';
import styled from 'styled-components';

import Column from 'components/common/Column';
import Text from 'components/common/Text';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import AuthButton from 'components/Button/AuthButton';
import { Delivery, DeliveryState } from 'types/api';
import Row from 'components/common/Row';
import CommonImage from 'components/common/CommonImage';
import { ICON_COPY_BLACK, ICON_NOTICE_BLUE } from 'assets/icons';
import DeliveryStep from './DeliveryStep';
import DeliveryInformation from './DeliveryInformation';
import EmptySettlement from 'containers/Settlement/components/EmptySettlement';
import DeliveryButton from './DeliveryButton';
import DeliveryConfirmDialog from './DeliveryConfirmDialog';
import { useMutationDeliveries } from '../graphQL/hooks/useMutationDeliveries';
import ConfirmDialog from 'components/Modal/ConfirmDialog';
import DeliveryCancelModal from './DeliveryCancelModal';
import { useDeliveryDetailHooks } from '../hooks/useDeliveryDetailHooks';

const FloatingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  padding: 16px 20px 24px 20px;
  margin-top: 20px;

  width: 100%;
  background-color: #fff;
  border-radius: 0 0 16px 16px;
  border-top: 1px solid #eaebec;
`;

const InformationContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  position: relative;
  width: 100%;

  overflow-x: scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoticeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  padding: 14px 12px;
  width: 100%;

  border-radius: 8px;
  border: 1px solid #eaebec;
  background: #fff;
`;

interface Props {
  isMobile: boolean;
  selectedDelivery: Delivery;
  handleCloseDeliveryDetail: () => void;
  handleRefetchDeliveryList: () => void;
}

const DeliveryDetail: FC<Props> = props => {
  const {
    isMobile,
    selectedDelivery,
    handleCloseDeliveryDetail,
    handleRefetchDeliveryList,
  } = props;

  const { handleRetreatPackingDeliveries } = useMutationDeliveries();
  const {
    isOpenConfirmDialog,
    isOpenRetreatDialog,
    isOpenCancelModal,
    isCancelDelivery,
    handleRequestCancelOrders,
    handleRetractCancelOrderRequests,
    handleCopyAddress,
    handleOpenConfirmDialog,
    handleCloseConfirmDialog,
    handleOpenRetreatDialog,
    handleCloseRetreatDialog,
    handleCloseCancelModal,
    handleRequestCancel,
  } = useDeliveryDetailHooks(selectedDelivery);

  const deliveryStatus = selectedDelivery && selectedDelivery.status;

  const handleAcceptConfirmDialog = () => {
    handleCloseConfirmDialog();

    handleRefetchDeliveryList();
    handleCloseDeliveryDetail();
  };

  const handleAcceptRetreatDialog = () => {
    handleRetreatPackingDeliveries([selectedDelivery.id], () => {
      handleCloseRetreatDialog();

      handleRefetchDeliveryList();
      handleCloseDeliveryDetail();
    });
  };

  const handleDeliveryRetractCancelOrderRequest = () => {
    handleRetractCancelOrderRequests(
      [selectedDelivery.order.items[0].dependedItems[0].transaction.id],
      () => {
        handleRefetchDeliveryList();
        handleCloseDeliveryDetail();
      },
    );
  };

  const renderDeliveryStatus = (): JSX.Element => {
    return (
      <InformationContainer>
        <DeliveryStep
          idx={1}
          title="결제완료"
          date={selectedDelivery.createdAt}
          isActive={selectedDelivery.createdAt !== null}
        />
        <DeliveryStep
          idx={2}
          title="배송 준비"
          date={selectedDelivery.packedAt}
          isActive={selectedDelivery.packedAt !== null}
        />
        <DeliveryStep
          idx={3}
          title="배송 시작"
          date={selectedDelivery.issuedAt}
          isActive={selectedDelivery.issuedAt !== null}
        />
        <DeliveryStep
          idx={4}
          title="배송 완료"
          date={selectedDelivery.completedAt}
          isActive={selectedDelivery.completedAt !== null}
        />
        <DeliveryStep
          idx={5}
          title="구매 확정"
          date={selectedDelivery.confirmedAt}
          isActive={selectedDelivery.confirmedAt !== null}
        />
      </InformationContainer>
    );
  };

  const renderDeliveryButton = (): JSX.Element => {
    if (!deliveryStatus) {
      return <></>;
    }

    if (
      isCancelDelivery ||
      deliveryStatus === DeliveryState.CONFIRMED ||
      deliveryStatus === DeliveryState.CANCELED
    ) {
      if (!isMobile) {
        return <></>;
      }

      return (
        <AuthButton
          isAbled={true}
          name="닫기"
          onClick={handleCloseDeliveryDetail}
          style={{
            padding: '12px 24px',
            width: 'auto',
            background: '#F4F4F5',
            color: '#70737C',
          }}
        />
      );
    }

    return (
      <DeliveryButton
        deliveryStatus={deliveryStatus}
        handleConfirmDialog={handleOpenConfirmDialog}
      />
    );
  };

  const renderContent = (): JSX.Element => {
    if (selectedDelivery) {
      return (
        <>
          <Row
            justifyContent="flex-start"
            style={{
              position: 'sticky',
              top: 0,
              padding: isMobile ? '24px 20px ' : '32px 40px',
              width: '100%',
              height: isMobile ? '84px' : '100px',
              background: '#fff',
              zIndex: 1,
            }}
          >
            <Text
              color={
                deliveryStatus === DeliveryState.CANCELED
                  ? '#ff4242'
                  : '#1b1c1e'
              }
              size="20px"
              weight={600}
            >
              {deliveryStatus === DeliveryState.CANCELED
                ? '취소된 배송(주문)이에요.'
                : isCancelDelivery
                ? '주문 취소가 요청된 주문이에요'
                : '배송 정보'}
            </Text>
          </Row>
          <Column
            align="flex-start"
            gap="24px"
            style={{
              padding: isMobile ? '0 20px 20px' : '0 40px 20px',
            }}
          >
            {!isCancelDelivery &&
              deliveryStatus !== DeliveryState.CANCELED &&
              renderDeliveryStatus()}
            {!isCancelDelivery && deliveryStatus === DeliveryState.PAID && (
              <NoticeContainer>
                <CommonImage
                  src={ICON_NOTICE_BLUE}
                  style={{ width: '16px', height: '16px' }}
                />
                <Text color="#46474C" size="13px">
                  배송 정보를 확인하고 준비가 완료되면 상태를 '배송준비'로
                  변경해 주세요.
                </Text>
              </NoticeContainer>
            )}
            <DeliveryInformation selectedDelivery={selectedDelivery} />
            {deliveryStatus === DeliveryState.PAID && (
              <Row
                gap="8px"
                justifyContent="flex-start"
                style={{ marginTop: '8px', width: '100%' }}
              >
                {isCancelDelivery ? (
                  <Row
                    gap="4px"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: '#F7F7F8',
                      cursor: 'pointer',
                    }}
                    onClick={handleDeliveryRetractCancelOrderRequest}
                  >
                    <Text color="#70737C" weight={600}>
                      주문 취소 요청 철회
                    </Text>
                  </Row>
                ) : (
                  <>
                    <Row
                      gap="4px"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        background: '#f4f4f5',
                        cursor: 'pointer',
                      }}
                      onClick={handleCopyAddress}
                    >
                      <CommonImage src={ICON_COPY_BLACK} />
                      <Text color="#46474C" weight={600}>
                        주소 복사
                      </Text>
                    </Row>
                    <Row
                      gap="4px"
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        background: '#FFE6E6',
                        cursor: 'pointer',
                      }}
                      onClick={handleRequestCancel}
                    >
                      <Text color="#FF4242" weight={600}>
                        주문 취소 요청
                      </Text>
                    </Row>
                  </>
                )}
              </Row>
            )}
            {deliveryStatus === DeliveryState.PACKING && (
              <Row
                gap="4px"
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: '#f4f4f5',
                  cursor: 'pointer',
                }}
                onClick={handleOpenRetreatDialog}
              >
                <Text color="#70737C" weight={600}>
                  배송 준비 철회
                </Text>
              </Row>
            )}
          </Column>
        </>
      );
    }

    return (
      <Column
        align="flex-start"
        gap="24px"
        style={{ padding: isMobile ? '28px 20px' : '28px 40px' }}
      >
        <Text color="#1b1c1e" size="20px">
          배송 정보
        </Text>
        <EmptySettlement textContent="선택한 배송 내역이 없어요" />
      </Column>
    );
  };

  const renderResponsiveContent = (): JSX.Element => {
    if (isMobile) {
      return (
        <ModalContainer
          isAbled={true}
          isBottomSheet={true}
          isMobile={isMobile}
          onAcceptText=""
          onClose={() => {}}
          onClick={() => {}}
          style={{ gap: '0' }}
        >
          {renderContent()}
          <FloatingContainer
            style={{
              justifyContent:
                deliveryStatus === DeliveryState.CONFIRMED ||
                deliveryStatus === DeliveryState.CANCELED ||
                isCancelDelivery
                  ? 'flex-end'
                  : 'space-between',
            }}
          >
            {deliveryStatus !== DeliveryState.CONFIRMED &&
              deliveryStatus !== DeliveryState.CANCELED &&
              !isCancelDelivery && (
                <Text
                  color="#70737C"
                  size="16px"
                  style={{ cursor: 'pointer' }}
                  onClick={handleCloseDeliveryDetail}
                >
                  닫기
                </Text>
              )}
            {renderDeliveryButton()}
          </FloatingContainer>
        </ModalContainer>
      );
    }

    return (
      <Column style={{ minWidth: '410px', maxWidth: '410px' }}>
        {renderContent()}
        <Row
          justifyContent="flex-end"
          style={{
            padding: isMobile ? '36px 20px 0' : '36px 40px 0',
            width: '100%',
          }}
        >
          {renderDeliveryButton()}
        </Row>
      </Column>
    );
  };

  return (
    <>
      {renderResponsiveContent()}
      {isOpenConfirmDialog && (
        <DeliveryConfirmDialog
          deliveryId={selectedDelivery.id}
          deliveryStatus={deliveryStatus}
          onClose={handleCloseConfirmDialog}
          onAccept={handleAcceptConfirmDialog}
        />
      )}
      {isOpenRetreatDialog && (
        <ConfirmDialog
          title={
            <Text
              color="#1b1c1e"
              size="20px"
              weight={600}
              style={{ lineHeight: '28px' }}
            >
              '배송 준비 {`->`} 결제 완료' <br />
              상태를 변경할까요?
            </Text>
          }
          onAcceptText="네, 변경할게요"
          onClose={handleCloseRetreatDialog}
          onAccept={handleAcceptRetreatDialog}
          buttonStyle={{ padding: '12px 24px', background: '#0071e9' }}
        />
      )}
      {isOpenCancelModal && (
        <DeliveryCancelModal
          isMobile={isMobile}
          orderItems={selectedDelivery.order.items}
          onClose={handleCloseCancelModal}
          handleRequestCancelOrders={handleRequestCancelOrders}
          handleRefetchDeliveryList={handleRefetchDeliveryList}
          handleCloseDeliveryDetail={handleCloseDeliveryDetail}
        />
      )}
    </>
  );
};

export default DeliveryDetail;
